/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { classNames } from '#utils/UNSAFE_classNames';
import { segmentStyles } from './themes/SegmentStyles.css';

type Props = {
  /**
   * True if the segment should be hidden.
   */
  isHidden?: boolean;

  /**
   * True if the segment should be highlighted.
   */
  isHighlighted?: boolean;

  /**
   * True if the literal is part of a time placeholder, such as hh:mm:ss:sss.
   * If the time is partially specified or complete, this is false.
   */
  isPlaceholder?: boolean;

  /**
   * The text to display for this segment.
   * If text is only spaces, then the text is not rendered, and instead the space is rendered using css.
   * This is more consistent in all browsers than whitespace is.
   */
  text: string;
};

/**
 * LiteralSegment is used to represent a literal separator in a time,
 * such as the ':' in 10:00:00:000 or the space between the time and the dayPeriod, 8:00 AM.
 */
export const LiteralSegment = ({ isHidden, isHighlighted, isPlaceholder, text }: Props) => {
  const isTextOnlyWhitespace = text.trim() === '';
  const spanClasses = classNames([
    segmentStyles.literalBase,
    isPlaceholder && segmentStyles.placeholder,
    isTextOnlyWhitespace && segmentStyles.space,
    isHighlighted && segmentStyles.highlighted,
    isHidden ? segmentStyles.hidden : segmentStyles.notHidden
  ]);

  return (
    <span aria-hidden="true" class={spanClasses}>
      {isTextOnlyWhitespace ? ' ' : text}
    </span>
  );
};
